import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '@/shared/api/client';
import { Button } from '@/shared/ui/button';
import { Badge } from '@/shared/ui/badge';
import EtatSignatureMandat from '@/shared/components/EtatSignatureMandat';
import {
  X, ExternalLink, Wifi, WifiOff, HelpCircle,
  FileText, Clock, MapPin, Mail, Phone, Activity
} from 'lucide-react';
import { ICON as TYPE_ICON, LABEL as TYPE_LABEL, COLOR as TYPE_COLOR } from '@/shared/constants/assetTypes';
import { CLIENT_STATUS_BANNER as STATUS_BANNER } from '@/shared/constants/statuses';

const CONN_COLOR = { connecte: 'text-emerald-500', deconnecte: 'text-red-500', inconnu: 'text-neutral-400' };

function fmt(iso) {
  if (!iso) return '-';
  try { return new Date(iso).toLocaleString('fr-FR', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }); }
  catch { return '-'; }
}

/**
 * Drawer latéral non-bloquant pour aperçu rapide d'un client.
 * - La liste derrière reste visible et cliquable (pas d'overlay).
 * - Se ferme via la croix, la touche Escape ou un clic en dehors (sur fond de la page,
 *   pas sur une autre ligne client — cliquer sur une autre ligne met à jour le drawer).
 */
export default function ClientDrawer({ clientId, onClose }) {
  const navigate = useNavigate();
  const [client, setClient] = useState(null);
  const [loading, setLoading] = useState(true);
  const drawerRef = useRef(null);

  useEffect(() => {
    if (!clientId) return;
    setLoading(true);
    setClient(null);
    api.get(`/clients/${clientId}`)
      .then(r => setClient(r.data))
      .catch(() => setClient(null))
      .finally(() => setLoading(false));
  }, [clientId]);

  // Fermeture via Escape
  useEffect(() => {
    if (!clientId) return;
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [clientId, onClose]);

  // Fermeture au clic en dehors du drawer.
  // Exception : un clic sur une ligne client (data-testid client-row-...) ne ferme pas,
  // ce qui permet de switcher l'apercu d'un client a l'autre sans fermeture intermediaire.
  useEffect(() => {
    if (!clientId) return;
    const onDown = (e) => {
      if (!drawerRef.current) return;
      if (drawerRef.current.contains(e.target)) return;
      const row = e.target.closest?.('[data-testid^="client-row-"]');
      if (row) return;
      // Ignorer clics sur les Selects/Dialogs Radix (rendus en portal hors du drawer)
      if (e.target.closest?.('[role="dialog"]') ||
          e.target.closest?.('[role="listbox"]') ||
          e.target.closest?.('[data-radix-popper-content-wrapper]')) return;
      onClose();
    };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [clientId, onClose]);

  if (!clientId) return null;

  const banner = client ? (STATUS_BANNER[client.statut] || STATUS_BANNER.prospect) : null;
  const assets = client?.assets || [];
  const mandats = client?.mandats || [];
  const connectedAssets = assets.filter(a => a.statut_connexion === 'connecte').length;
  // ⚠️ « SIGNÉ » SE LIT SUR LE VOLET DE SIGNATURE, PAS SUR `statut`. Le test d'origine
  // retenait `statut in (actif, signe, mandat_signe)` — or `statut` dit que le MANDAT
  // est en vigueur, pas que quelqu'un l'a signé. Depuis le canal à distance, un mandat
  // qui ATTEND la signature du client garde `statut: actif` : ce drawer l'affichait donc
  // « Signé », et la ligne du dessous « Signé le - », `date_signature` ayant justement
  // été retirée pour que la pièce ne se date pas avant le trait.
  // L'état vient désormais du serveur, en un seul vocabulaire.
  const estSigne = (m) => m.etat_signature?.code === 'signe'
    // Repli pour les mandats servis par une route qui ne porte pas encore l'état : on
    // retombe sur la SIGNATURE, jamais sur le statut du mandat.
    || (!m.etat_signature && !!m.date_signature);
  const hasSignedMandat = mandats.some(estSigne);
  const symphonicsStatus = client?.symphonics_sync_status || 'not_sent';
  const symphonicsStatusLabel = {
    synced: 'Envoyé',
    failed: 'Erreur',
    dry_run: 'Dry-run OK',
    not_sent: 'Non envoyé',
  }[symphonicsStatus] || symphonicsStatus;
  const symphonicsStatusClass = symphonicsStatus === 'synced' ? 'bg-emerald-50 text-emerald-700 border-emerald-200' :
    symphonicsStatus === 'failed' ? 'bg-red-50 text-red-700 border-red-200' :
    symphonicsStatus === 'dry_run' ? 'bg-amber-50 text-amber-700 border-amber-200' :
    'bg-neutral-50 text-neutral-600 border-neutral-200';
  const symphonicsMandatStatus = hasSignedMandat ? 'Signé' : 'En attente';
  const displayedMandatCount = mandats.length + (hasSignedMandat ? 1 : 0);

  return (
    <>
      {/* Backdrop transparent : ferme uniquement quand on clique a cote du drawer.
          Aucun fond sombre : la liste derriere reste pleinement visible et la barre
          de filtres reste accessible. */}
      <div
        className="fixed inset-0 z-40 pointer-events-none"
        aria-hidden="true"
      />
      <aside
        ref={drawerRef}
        className="fixed right-0 top-0 z-50 h-screen w-full sm:w-[420px] bg-white border-l border-neutral-200 shadow-2xl flex flex-col animate-slide-in-right"
        style={{ animation: 'slideInRight 250ms ease-out' }}
        data-testid="client-drawer"
        role="dialog"
        aria-label="Aperçu client"
      >
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-neutral-100 flex-shrink-0">
          <div className="flex items-center gap-2 min-w-0">
            <span className="text-[11px] uppercase tracking-wider text-neutral-400 font-medium">Aperçu client</span>
          </div>
          <div className="flex items-center gap-2">
            {client && (
              <Button
                size="sm"
                variant="outline"
                className="h-8 text-[11px]"
                onClick={() => { navigate(`/clients/${client.id}`); onClose(); }}
                data-testid="drawer-open-full-page-btn"
              >
                <ExternalLink className="w-3 h-3 mr-1" /> Ouvrir la page complète
              </Button>
            )}
            <button
              onClick={onClose}
              className="w-7 h-7 rounded-md flex items-center justify-center text-neutral-400 hover:text-neutral-900 hover:bg-neutral-100 transition-colors"
              data-testid="drawer-close-btn"
              aria-label="Fermer"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto">
          {loading ? (
            <div className="flex items-center justify-center h-32" data-testid="drawer-loading">
              <div className="w-5 h-5 border-2 border-neutral-300 border-t-neutral-900 rounded-full animate-spin" />
            </div>
          ) : !client ? (
            <div className="p-6 text-center text-neutral-400" data-testid="drawer-error">Client introuvable</div>
          ) : (
            <div className="p-5 space-y-5">
              {/* Identite + bandeau statut */}
              <div>
                <h2 className="text-lg font-semibold text-neutral-900 leading-tight" data-testid="drawer-client-name">
                  {client.civilite} {client.prenom} {client.nom}
                </h2>
                <p className="text-[12px] text-neutral-500 flex items-center gap-1 mt-1">
                  <MapPin className="w-3 h-3" />
                  {client.code_postal} {client.ville}{client.departement ? ` (${client.departement})` : ''}
                </p>
              </div>

              <div className={`rounded-lg ${banner.bg} ring-1 ${banner.ring} px-3 py-2 flex items-center gap-2`} data-testid="drawer-status-banner">
                <span className={`w-2 h-2 rounded-full ${banner.dot} animate-pulse`} />
                <span className={`text-[12px] font-semibold uppercase tracking-wider ${banner.text}`}>
                  {banner.label}
                </span>
              </div>

              {/* Coordonnees */}
              <div className="space-y-1.5 text-[12px]">
                {client.email && (
                  <p className="flex items-center gap-2 text-neutral-700">
                    <Mail className="w-3 h-3 text-neutral-400" /> {client.email}
                  </p>
                )}
                {client.telephone && (
                  <p className="flex items-center gap-2 text-neutral-700">
                    <Phone className="w-3 h-3 text-neutral-400" /> {client.telephone}
                  </p>
                )}
                {client.pdl_prm && (
                  <p className="flex items-center gap-2 text-neutral-500">
                    <span className="text-[10px] uppercase tracking-wider text-neutral-400">PDL</span>
                    <span className="font-mono text-[11px]">{client.pdl_prm}</span>
                  </p>
                )}
              </div>

              {/* Bloc resume 4 tuiles */}
              <div className="grid grid-cols-2 gap-2" data-testid="drawer-summary">
                <SummaryTile
                  label="Actifs"
                  value={`${assets.length}`}
                  sub={`${connectedAssets} connecté${connectedAssets > 1 ? 's' : ''}`}
                />
                <SummaryTile
                  label="Mandat"
                  value={client.mandat_status_summary === 'actif' ? 'Actif' : client.mandat_status_summary === 'signe' ? 'Signé' : 'Aucun'}
                  sub={`${displayedMandatCount} mandat${displayedMandatCount > 1 ? 's' : ''}`}
                />
                <SummaryTile
                  label="Agrégateur"
                  value={client.aggregateur || '—'}
                  sub={`Symphonics: ${symphonicsStatusLabel}`}
                />
                <SummaryTile
                  label="Dernière activité"
                  value={client.derniere_activite ? fmt(client.derniere_activite).split(',')[0] : '—'}
                  sub={client.derniere_activite ? fmt(client.derniere_activite).split(',')[1]?.trim() : ''}
                />
              </div>

              {/* Liste actifs (compact) */}
              <div>
                <p className="text-[11px] uppercase tracking-wider text-neutral-400 font-medium mb-2">
                  Actifs ({assets.length})
                </p>
                {assets.length === 0 ? (
                  <p className="text-[12px] text-neutral-400 text-center py-3 border border-dashed border-neutral-200 rounded-md">
                    Aucun actif
                  </p>
                ) : (
                  <div className="space-y-1.5">
                    {assets.map(a => {
                      const Icon = TYPE_ICON[a.type] || HelpCircle;
                      const ConnIcon = a.statut_connexion === 'connecte' ? Wifi : a.statut_connexion === 'deconnecte' ? WifiOff : HelpCircle;
                      return (
                        <div
                          key={a.id}
                          className="flex items-center gap-2 p-2.5 rounded-md border border-neutral-200/80 bg-white"
                          data-testid={`drawer-asset-${a.id}`}
                        >
                          <Icon className={`w-3.5 h-3.5 flex-shrink-0 ${TYPE_COLOR[a.type] || 'text-neutral-400'}`} />
                          <div className="flex-1 min-w-0">
                            <p className="text-[12px] font-medium text-neutral-900 truncate">
                              {TYPE_LABEL[a.type] || a.type} — {a.marque} {a.modele}
                            </p>
                            <p className="text-[10px] text-neutral-400 truncate flex items-center gap-1">
                              <Clock className="w-2.5 h-2.5" /> {a.dernier_contact ? fmt(a.dernier_contact) : '—'}
                            </p>
                          </div>
                          <ConnIcon className={`w-3.5 h-3.5 flex-shrink-0 ${CONN_COLOR[a.statut_connexion] || 'text-neutral-400'}`} />
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>

              {/* Mandats */}
              {displayedMandatCount > 0 && (
                <div>
                  <p className="text-[11px] uppercase tracking-wider text-neutral-400 font-medium mb-2">
                    Mandats ({displayedMandatCount})
                  </p>
                  <div className="space-y-1.5">
                    {hasSignedMandat && (
                      <div className="flex items-center gap-2 p-2.5 rounded-md border border-cyan-200 bg-cyan-50/40" data-testid="drawer-symphonics-mandat-card">
                        <FileText className="w-3.5 h-3.5 text-cyan-600 flex-shrink-0" />
                        <div className="flex-1 min-w-0">
                          <p className="text-[12px] font-medium text-neutral-900">Mandat Symphonics</p>
                          <p className="text-[10px] text-neutral-500">Envoi CRM: {symphonicsStatusLabel}</p>
                        </div>
                        <div className="flex items-center gap-1.5 flex-shrink-0">
                          <Badge variant="outline" className="bg-cyan-50 text-cyan-700 border-cyan-200 text-[10px]">
                            {symphonicsMandatStatus}
                          </Badge>
                          <Badge variant="outline" className={`${symphonicsStatusClass} text-[10px]`}>
                            {symphonicsStatusLabel}
                          </Badge>
                        </div>
                      </div>
                    )}
                    {mandats.slice(0, 3).map(m => (
                      <div key={m.id} className="flex items-center gap-2 p-2.5 rounded-md border border-neutral-200/80 bg-white" data-testid={`drawer-mandat-${m.id}`}>
                        <FileText className="w-3.5 h-3.5 text-neutral-400 flex-shrink-0" />
                        <div className="flex-1 min-w-0">
                          <p className="text-[12px] font-medium text-neutral-900">Mandat de flexibilité</p>
                          {/* La date n'est affichée QUE si le mandat est signé. « Signé
                              le - » sur un dossier qui attend son client était le
                              défaut mesuré le 07/09. */}
                          {estSigne(m)
                            ? <p className="text-[10px] text-neutral-400">Signé le {fmt(m.date_signature)}</p>
                            : <EtatSignatureMandat etat={m.etat_signature} compact />}
                        </div>
                        <Badge variant="outline" className={
                          estSigne(m) ? 'bg-emerald-50 text-emerald-700 border-emerald-200 text-[10px]' :
                          'bg-neutral-50 text-neutral-600 border-neutral-200 text-[10px]'
                        }>
                          {m.statut}
                        </Badge>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Derniere action */}
              {client.derniere_action && (
                <div className="rounded-md bg-neutral-50/70 p-3" data-testid="drawer-last-action">
                  <p className="text-[11px] uppercase tracking-wider text-neutral-400 font-medium mb-1">
                    Dernière action
                  </p>
                  <p className="text-[12px] text-neutral-700 flex items-start gap-1.5">
                    <Activity className="w-3 h-3 text-neutral-400 mt-0.5 flex-shrink-0" />
                    <span>{client.derniere_action}</span>
                  </p>
                </div>
              )}
            </div>
          )}
        </div>
      </aside>

      <style>{`
        @keyframes slideInRight {
          from { transform: translateX(100%); opacity: 0.4; }
          to { transform: translateX(0); opacity: 1; }
        }
      `}</style>
    </>
  );
}

function SummaryTile({ label, value, sub }) {
  return (
    <div className="rounded-md border border-neutral-200/80 bg-white p-2.5">
      <p className="text-[9px] uppercase tracking-wider text-neutral-400 font-medium">{label}</p>
      <p className="text-[13px] font-semibold text-neutral-900 mt-0.5 leading-tight truncate">{value}</p>
      {sub && <p className="text-[10px] text-neutral-400 mt-0.5 truncate">{sub}</p>}
    </div>
  );
}
